/**
 * M7 S9: the comms engine (spec §2.7, §3.1, §11). A conversation is a graph of comms nodes (`comms/*.json`): a
 * speaker, a placeholder portrait, 1–3 lines and up to 4 replies. A reply without `next` ends the conversation.
 *
 * - **"Show [log]" replies** appear only while the player holds a relevant log: one of the reply's `show.logs`, or
 *   any held log matching every one of `show.thread` / `show.link` given. A log is held when it is in the ledger
 *   (read or unread), or when it is a mission item's log and the item is `held` or a `copy`. Nothing fails without
 *   a Show reply (spec §2.7): every node keeps a plain reply, which `commsGraphProblems` checks.
 * - **Reply effects are `StoryEvent`s**: choosing a reply yields `{ type: 'commsReply', nodeId, replyId }`, which the
 *   mission runtime (S6), the claims (S7) and the safety net consume. The engine itself changes nothing.
 * - Speaker names, faction tints and portrait ids live in `comms/common.json speakers` (names are placeholders until
 *   the naming pass, spec §15 item 7), so the engine never holds a name.
 *
 * Pure and event-driven (never per frame); the data is passed in so tests use their own.
 */
import commsCommonJson from '../data/story/comms/common.json'
import type { Career } from '../career/types'
import { z } from 'zod'
import { storyOf } from './state'
import {
  commsSpeakerSchema,
  contactsSchema,
  speakerSchema,
  type CommsNode,
  type CommsSpeaker,
  type ContactsConfig,
  type EvidenceLog,
} from './schemas'
import type { FactionId } from '../strategic/types'
import { SPEAKERS, type CareerStage, type Speaker, type StoryEffect, type StoryEvent } from './types'

/** The fields of a log the engine reads (thread/link matching and the mission-item rule). */
export type CommsLogs = ReadonlyMap<string, Pick<EvidenceLog, 'id' | 'thread' | 'link' | 'item'>>

/** What the engine reads from the story data (`StoryData` satisfies it). */
export interface CommsData {
  commsById: ReadonlyMap<string, CommsNode>
  logsById: CommsLogs
}

export interface CommsConfig {
  speakers: Readonly<Record<Speaker, CommsSpeaker>>
  contacts: ContactsConfig
}

const commonExtrasSchema = z.object({
  speakers: z.partialRecord(speakerSchema, commsSpeakerSchema),
  contacts: contactsSchema,
})

/** Parses `common.json`'s `speakers` and `contacts` (every speaker named; both blocks required in `common.json`). */
export function loadCommsConfig(raw: unknown): CommsConfig {
  const result = commonExtrasSchema.safeParse(raw)
  if (!result.success) {
    const issue = result.error.issues[0]
    const where = issue && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)'
    throw new Error(`story data: comms/common.json: ${where}: ${issue?.message ?? 'invalid'}`)
  }
  const speakers = {} as Record<Speaker, CommsSpeaker>
  for (const s of SPEAKERS) {
    const entry = result.data.speakers[s]
    if (entry === undefined) throw new Error(`story data: comms/common.json: speakers.${s}: missing`)
    speakers[s] = entry
  }
  return { speakers, contacts: result.data.contacts }
}

let cached: CommsConfig | undefined

/** The shipped comms config, parsed once on first use. */
export function commsConfig(): CommsConfig {
  cached ??= loadCommsConfig(commsCommonJson)
  return cached
}

// ---------------------------------------------------------------------------------------------
// Node ids other tasks open (S7 claims and the safety net, S14 nudges and the hint ladder)
// ---------------------------------------------------------------------------------------------

/** Kell's one nudge for `tier` (1–4; the `kell_nudge_<tier>` flag marks it sent). */
export function nudgeNodeId(tier: number): string {
  return `kell.nudge.${Math.max(1, tier)}`
}

/** A rung of Kell's hint ladder for `tier` (1–4), rung 1 first. */
export function hintNodeId(tier: number, rung: number): string {
  return `kell.hint.${Math.max(1, tier)}.${rung}`
}

/** A speaker id as a data-id segment (`mutualArchivist` → `mutual_archivist`; node ids are lower-case). */
export function speakerKey(speaker: Speaker): string {
  return speaker.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
}

/** The recipient's answer to a stated claim: it holds, or "this doesn't hold up" (never a count, spec §2.3). */
export function claimReplyNodeId(tier: number, speaker: Speaker, holds: boolean): string {
  return `claim.${tier}.${speakerKey(speaker)}.${holds ? 'holds' : 'fails'}`
}

/** The safety-net offer node for Kell or the archivist (spec §2.3). */
export const SAFETY_NET_NODE: Readonly<Partial<Record<Speaker, string>>> = Object.freeze({
  kell: 'kell.safety_net',
  mutualArchivist: 'archivist.safety_net',
})

// ---------------------------------------------------------------------------------------------
// Held logs and Show replies
// ---------------------------------------------------------------------------------------------

type ShowSpec = NonNullable<CommsNode['replies'][number]['show']>

/** Whether the player holds `logId`: in the ledger, or a mission item's log with the item held or copied. */
export function holdsLog(career: Career, logs: CommsLogs, logId: string): boolean {
  const s = storyOf(career)
  if (s.readLogs.includes(logId) || s.unreadLogs.includes(logId)) return true
  const item = logs.get(logId)?.item
  if (item === undefined) return false
  return s.items.some((it) => it.id === item && (it.state === 'held' || it.state === 'copy'))
}

/** Held log ids: the ledger in reading order, then mission-item logs not in the ledger. */
export function heldLogIds(career: Career, logs: CommsLogs): string[] {
  const s = storyOf(career)
  const out = [...s.readLogs, ...s.unreadLogs]
  const seen = new Set(out)
  for (const log of logs.values()) {
    if (!seen.has(log.id) && log.item !== undefined && holdsLog(career, logs, log.id)) out.push(log.id)
  }
  return out
}

/** Whether a Show spec has any criterion at all (one without is never shown; the graph check flags it). */
function showHasCriteria(show: ShowSpec): boolean {
  return show.logs.length > 0 || show.thread !== undefined || show.link !== undefined
}

/** The held log a Show reply would present (the first match in `heldLogIds` order), or null. */
export function showLogFor(career: Career, logs: CommsLogs, show: ShowSpec): string | null {
  for (const logId of show.logs) if (holdsLog(career, logs, logId)) return logId
  if (show.thread === undefined && show.link === undefined) return null
  for (const logId of heldLogIds(career, logs)) {
    const log = logs.get(logId)
    if (log === undefined) continue
    if (show.thread !== undefined && log.thread !== show.thread) continue
    if (show.link !== undefined && log.link !== show.link) continue
    return logId
  }
  return null
}

export function replyVisible(career: Career, logs: CommsLogs, reply: CommsNode['replies'][number]): boolean {
  if (reply.show === undefined) return true
  return showHasCriteria(reply.show) && showLogFor(career, logs, reply.show) !== null
}

// ---------------------------------------------------------------------------------------------
// The view and the reply
// ---------------------------------------------------------------------------------------------

export interface CommsReplyView {
  id: string
  text: string
  /** A "Show [log]" reply: the held log it presents. */
  showLog?: string
  /** False = this reply ends the conversation. */
  continues: boolean
}

/** What the comms panel (S31) draws for one node. */
export interface CommsView {
  nodeId: string
  speaker: Speaker
  name: string
  faction?: FactionId
  portrait: string
  lines: readonly string[]
  /** Only the replies the player can pick now (Show replies gated by held logs). */
  replies: CommsReplyView[]
}

/**
 * D-425: the node's lines at `stage`. A corporation takes its own variant, else the owned one when the lease is paid
 * off; an owned pilot takes the owned variant; otherwise the leased `lines`.
 */
export function nodeLines(node: CommsNode, stage: CareerStage, leaseOwned: boolean): readonly string[] {
  const v = node.variants
  if (v === undefined) return node.lines
  if ((stage === 'corporation' || stage === 'fleetNotes') && v.corporation !== undefined) return v.corporation
  if ((stage === 'owned' || leaseOwned) && v.owned !== undefined) return v.owned
  return node.lines
}

/**
 * The node as the player sees it now, or null for an unknown node id. `stage` is the career stage now (the story
 * link passes the worker's; absent = a pilot without a corporation, from the lease).
 */
export function viewNode(
  career: Career,
  data: CommsData,
  nodeId: string,
  cfg: CommsConfig = commsConfig(),
  stage: CareerStage = career.lease.owned ? 'owned' : 'leased',
): CommsView | null {
  const node = data.commsById.get(nodeId)
  if (node === undefined) return null
  const who = cfg.speakers[node.speaker]
  const replies: CommsReplyView[] = []
  for (const r of node.replies) {
    if (!replyVisible(career, data.logsById, r)) continue
    const view: CommsReplyView = { id: r.id, text: r.text, continues: r.next !== undefined }
    if (r.show !== undefined) {
      const log = showLogFor(career, data.logsById, r.show)
      if (log !== null) view.showLog = log
    }
    replies.push(view)
  }
  const out: CommsView = {
    nodeId: node.id,
    speaker: node.speaker,
    name: who.name,
    portrait: node.portrait ?? who.portrait,
    lines: nodeLines(node, stage, career.lease.owned),
    replies,
  }
  if (who.faction !== undefined) out.faction = who.faction
  return out
}

export interface ReplyResult {
  /** The story events the reply raises (always the `commsReply`; mission steps and claims read it). */
  events: StoryEvent[]
  /** The next node, or null when the conversation ends. */
  next: string | null
  effects: StoryEffect[]
}

/**
 * Picks `replyId` at `nodeId`. Null when the node or reply is unknown, or the reply isn't visible (a Show reply
 * without the log): the caller ignores the pick.
 */
export function chooseReply(career: Career, data: CommsData, nodeId: string, replyId: string): ReplyResult | null {
  const node = data.commsById.get(nodeId)
  const reply = node?.replies.find((r) => r.id === replyId)
  if (node === undefined || reply === undefined) return null
  if (!replyVisible(career, data.logsById, reply)) return null
  const events: StoryEvent[] = [{ type: 'commsReply', nodeId, replyId }]
  const next = reply.next ?? null
  const effects: StoryEffect[] = []
  if (next !== null) {
    const nextNode = data.commsById.get(next)
    if (nextNode !== undefined) effects.push({ type: 'comms', speaker: nextNode.speaker, nodeId: next })
  }
  return { events, next, effects }
}

/** The effect that opens `nodeId` on the comms panel (null for an unknown node). */
export function openComms(data: Pick<CommsData, 'commsById'>, nodeId: string): StoryEffect | null {
  const node = data.commsById.get(nodeId)
  return node === undefined ? null : { type: 'comms', speaker: node.speaker, nodeId }
}

// ---------------------------------------------------------------------------------------------
// Graph checks (the data tests run them over every comms file)
// ---------------------------------------------------------------------------------------------

/**
 * Problems with a node graph: a node with no plain (non-Show) reply (it could dead-end without the log, spec §2.7),
 * a Show reply with no criterion, a `next` to an unknown node, or a node that can't reach an exit (a reply without
 * `next`). Empty = sound.
 */
export function commsGraphProblems(nodes: readonly CommsNode[], logs: CommsLogs): string[] {
  const problems: string[] = []
  const byId = new Map(nodes.map((n) => [n.id, n]))
  for (const node of nodes) {
    if (!node.replies.some((r) => r.show === undefined)) problems.push(`${node.id}: every reply is a Show reply`)
    for (const r of node.replies) {
      if (r.show !== undefined && !showHasCriteria(r.show)) problems.push(`${node.id}.${r.id}: a Show reply with no log, thread or link`)
      for (const logId of r.show?.logs ?? []) if (!logs.has(logId)) problems.push(`${node.id}.${r.id}: no log '${logId}'`)
      if (r.next !== undefined && !byId.has(r.next)) problems.push(`${node.id}.${r.id}: no node '${r.next}'`)
    }
  }
  // Reverse reachability from the exits, over plain replies only (a Show reply may be hidden).
  const exits = new Set<string>()
  for (const node of nodes) if (node.replies.some((r) => r.show === undefined && r.next === undefined)) exits.add(node.id)
  let grew = true
  while (grew) {
    grew = false
    for (const node of nodes) {
      if (exits.has(node.id)) continue
      if (node.replies.some((r) => r.show === undefined && r.next !== undefined && exits.has(r.next))) {
        exits.add(node.id)
        grew = true
      }
    }
  }
  for (const node of nodes) if (!exits.has(node.id)) problems.push(`${node.id}: no path to an exit`)
  return problems
}
